import { ControllerInterface } from "csi/action/Controller";
import JsonResponse from "csi/comm/JsonResponse";
import locale from "csi/locale";
import { getAggregateStoreValuesAsComponentItems } from "csi/middleware/aggregateStoreUtility";
import { ControllerSaveFunction } from "csi/middleware/common";
import { submitWriter } from "csi/service/writerService";
import { C8Node, ICache, cnl } from "csi/typings";
import xml from "csi/xml";
import Writer from "csi/xml/Writer";
import { ILocaleProvider } from "designSystem/locale";
import { green } from "designSystem/styles/color";
import { ComponentItem } from "designSystem/types/component-item-types";
import { ColorProductSource, ColorSourceStatus, ColorwayAllocationManageStore, ProductSourceStore } from "./ColorwayAllocationManageStore";
import reflection from "csi/reflection";

/** Create and initialize ColorwayAllocationManageStore */
export async function createColorwayAllocationManageStore (ac: ControllerInterface, locale: ILocaleProvider, onClose: () => void, onSave: ControllerSaveFunction): Promise<ColorwayAllocationManageStore> {
	const cache = ac.getCache();
	const actionParentNode = ac.getParent(); 
	const rc = reflection.getNodeClass(actionParentNode);
	const sourcingNode = rc.isDerivedFrom("Product")
		? cache.get(actionParentNode["ProductSourcing"])
		: actionParentNode;

	let productSources = cache.getNodes(ac.m_urlNode ? [ac.m_urlNode] : sourcingNode?.ProductSources);
	productSources = productSources.filter( (ps) => { 
		const sourceNode = cache.getNode(ps.SupplierFactory) || cache.getNode(ps.Supplier);
		return xml.nodeHasRight(sourceNode, xml.RIGHT_READ);
	});
	const productSourceStores = await convertProductSourceNodesToStores(productSources, sourcingNode.$Type, locale, cache);
	const formData = ac.getFormData();
	const actionSourceNode = ac.getNode() || sourcingNode;
	const store = new ColorwayAllocationManageStore(cache, locale, onClose, onSave, productSourceStores, sourcingNode, formData, actionSourceNode);
	return store;
};

/** Convert list of product source node to a list of ProductSourceStore */
async function convertProductSourceNodesToStores (productSources: C8Node[], sourcingType: string, locale: ILocaleProvider, cache: ICache): Promise<ProductSourceStore[]> {
	const productSourceStores: ProductSourceStore[] = [];
	for (const productSourceNode of productSources) {
		const store = await createProductSourceStore(productSourceNode, sourcingType, locale, cache);
		productSourceStores.push(store);
	}
	return productSourceStores;
};

/** Create and initialize ProductSourceStore */
async function createProductSourceStore (productSource: C8Node, sourcingType:string, locale: ILocaleProvider, cache: ICache): Promise<ProductSourceStore> {
	const sourceNode = cache.get(productSource.SupplierFactory) || cache.get(productSource.Supplier);
	const options = await getProductSourceColorwayOptions(productSource.$URL);
	//Filter out invalid options
	const validOptions = options.filter((option) => !option.invalid);
	// convertOptions to ColorProductSource[];
	const colorSourceOptions: ColorProductSource[] = convertOptionsToColorProductSource(validOptions, productSource, cache);
	const values = colorSourceOptions.filter((option) => option.disabled && option.node);
	const store = new ProductSourceStore(productSource.$URL, locale.getNodeName(sourceNode), sourcingType, colorSourceOptions, values);

	return store;
};

/** Convert list of options to a list of ColorProductSource */
function convertOptionsToColorProductSource (options: ComponentItem<any>[], productSource: C8Node, cache: ICache): ColorProductSource[] {
	const colorProductSources = cache.getNodes(productSource.ColorSources);
	const colorProductSourcesMap = new Map<string, C8Node>(colorProductSources.map((cps) => [cps.Colorway, cps]));
	const colorSourceOptions: ColorProductSource[] =  options.map((option) => {
		const colorwayNode: C8Node = cache.get(option.id);
		const colorSource = colorProductSourcesMap.get(option.id);
		// Not resoult found case
		if (colorProductSourcesMap.has(option.id)) {
			return  {
				...option,
				cnl: colorSource.$URL,
				node: colorwayNode,
				status: colorSource.Active ? ColorSourceStatus.Active : ColorSourceStatus.Inactive,
				statusColor: green[600],
				active: colorSource.Active,
				updated: false, 
			} ;
		} else { /** options not associated - to be added  */
			return  { 
				...option,
				node: colorwayNode,
				status: ColorSourceStatus.Added,
				active: true,
				updated: true, 
			} ;
		}
	});
	return colorSourceOptions;
}

/** Get colorway options for  Product Source -> ColorSources aggregate store. */
async function getProductSourceColorwayOptions (productSource: cnl) : Promise<ComponentItem<any>[]> {
	const aggregateStoreParams = {
		attributeId: "ColorSources",
		locale: locale.getLocale(),
		nodeType: "ProductSource",
		sourceUrl: productSource,
	};

	return getAggregateStoreValuesAsComponentItems(aggregateStoreParams);
};

/** Send server request to enable Colorway Allocation */
export async function setColorAllocation(
	sourcingURL: cnl,
	supplierRequestUrls: cnl[],
	newcolorAllocationMap: Record<cnl, string>,
	activeColorAllocationMap: Record<cnl, string>,
	inactiveColorAllocationMap: Record<cnl, string>, 
): Promise<JsonResponse> {
	const writer = new Writer();
	writer
		.callMethod("Sourcing", "SetColorAllocation")
		.addParameter("URL", xml.ATTR_REF, sourcingURL)
		.addParameter("SupplierRequests", xml.ATTR_REFLIST, supplierRequestUrls)
		.addParameter("NewColorAllocationMap", xml.ATTR_STRINGMAP, newcolorAllocationMap)
		.addParameter("ActiveColorAllocationMap", xml.ATTR_STRINGMAP, activeColorAllocationMap)
		.addParameter("InactiveColorAllocationMap", xml.ATTR_STRINGMAP, inactiveColorAllocationMap);
	return submitWriter(writer);
}